import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AnimalContainer,
  Author,
  ChatContainer,
  ChatHeader,
  ChatMessagesList,
  EmptyChat,
  InputContainer,
  Message,
  MessageContainer,
  MessageWrapper,
  TextInput,
  Time,
} from "./Chat.styles";
import { Badge, Button, Fab, IconButton, rgbToHex, Zoom } from "@mui/material";
import { useSessionContext } from "../../contexts/SessionContext";
import { useGameContext } from "../../contexts/GameContext";
import { useSendMessage } from "../../hooks/Game/useSendMessage";
import dayjs from "dayjs";
import { useSoundContext } from "../../contexts/SoundContext";
import AnimalBg from "../AnimalBg/AnimalBg";
import { shouldShowShadow } from "./helpers";
import parseCSSColor from "parse-css-color";
import Icon from "../ui/Icon";
import { colors } from "../../style/tokens";

const Chat: React.FC = () => {
  const ref = useRef<HTMLUListElement>(null);
  const { user_id, user } = useSessionContext();
  const [message, setMessage] = useState("");
  const [open, setOpen] = useState(false);
  const [lastRead, setLastRead] = useState(0);
  const lastNotification = useRef(0);
  const { code, chatMessages, finished, players } = useGameContext();
  const { playBubbleAudio } = useSoundContext();

  const { loading, sendMessage } = useSendMessage(code);

  const handleSend = async () => {
    if (message.trim() && user) {
      await sendMessage(message.trim());
      setMessage("");
    }
  };

  const [playerColors, playerAvatars] = useMemo(
    () =>
      players.reduce(
        ([colors, avatars], player) => [
          {
            ...colors,
            [player?.id || ""]: player.online
              ? rgbToHex(
                `rgb(${(parseCSSColor(player.color || "")?.values || [0, 0, 0]).join(",")})`,
              )
              : colors.inkFaint,
          },
          {
            ...avatars,
            [player?.id || ""]: player.avatar,
          },
        ],
        [{}, {}] as any,
      ),
    [players],
  );

  useEffect(() => {
    ref.current?.scrollTo({
      top: ref.current.scrollHeight,
      behavior: "smooth",
    });
  }, [chatMessages]);

  useEffect(() => {
    if (open) {
      ref.current?.scrollTo({
        top: ref.current.scrollHeight,
        behavior: "smooth",
      });
      setLastRead(chatMessages.length);
    }
  }, [chatMessages.length, open]);

  useEffect(() => {
    if (lastRead) {
      sessionStorage.setItem(`last-read-${code}`, `${lastRead}`);
    }
  }, [code, lastRead]);

  useEffect(() => {
    const lastFromSession = sessionStorage.getItem(`last-read-${code}`);
    if (lastFromSession) {
      setLastRead(+lastFromSession);
    }
  }, [code, lastRead]);

  const unread =
    chatMessages?.length - lastRead > 0 ? chatMessages?.length - lastRead : 0;
  const lastMessageUserId = [...chatMessages].pop()?.user.id;

  useEffect(() => {
    if (
      unread &&
      lastMessageUserId !== user_id &&
      !open &&
      lastNotification.current < chatMessages?.length
    ) {
      playBubbleAudio?.();
      lastNotification.current = chatMessages?.length;
    }
  }, [
    chatMessages?.length,
    lastMessageUserId,
    open,
    playBubbleAudio,
    unread,
    user_id,
  ]);

  return (
    <ChatContainer $open={open}>
      {!finished && (
        <Zoom
          in={!open}
          timeout={100}
          unmountOnExit
          style={{
            transitionDelay: "100ms",
          }}
        >
          <Fab
            color="inherit"
            onClick={() => setOpen(true)}
            size="medium"
            aria-label="Chat"
          >
            <Badge
              variant="standard"
              color="error"
              badgeContent={unread}
              showZero={false}
              style={{ cursor: "pointer" }}
            >
              <Icon name="chat" size={22} />
            </Badge>
          </Fab>
        </Zoom>
      )}

      {open && (
        <ChatHeader>
          <h2>
            <Icon name="chat" />
            Chat
          </h2>

          <IconButton onClick={() => setOpen(false)} aria-label="Fechar">
            <Icon name="close" />
          </IconButton>
        </ChatHeader>
      )}

      {open && (
        <>
          <ChatMessagesList ref={ref}>
            {!chatMessages.length && (
              <EmptyChat>
                <Icon name="chat" size={32} />
                Ninguém falou nada ainda. Quebra o gelo!
              </EmptyChat>
            )}
            {chatMessages.map(({ user, text, date }, i) => {
              const sent = user.id === user_id;
              const isLast =
                !chatMessages?.[i + 1]?.user?.id ||
                chatMessages[i + 1].user.id !== user.id;

              return (
                <MessageWrapper $sent={sent} key={i}>
                  <AnimalContainer>
                    {isLast && (
                      <AnimalBg
                        circle
                        size="28px"
                        name={playerAvatars[user.id]}
                        color={playerColors[user.id]}
                      />
                    )}
                  </AnimalContainer>
                  <MessageContainer $sent={sent} $isLast={isLast}>
                    <Message $sent={sent} $isLast={isLast}>
                      {!sent && (
                        <Author
                          $color={
                            shouldShowShadow(playerColors[user.id])
                              ? colors.ink
                              : playerColors[user.id]
                          }
                        >
                          {user.name}
                        </Author>
                      )}
                      {text}
                    </Message>
                    {isLast && (
                      <Time>
                        {dayjs(date).format(
                          dayjs(date).isSame(new Date(), "date")
                            ? "HH:mm"
                            : "DD/MM/YYYY HH:mm",
                        )}
                      </Time>
                    )}
                  </MessageContainer>
                </MessageWrapper>
              );
            })}
          </ChatMessagesList>

          <InputContainer>
            <TextInput
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Manda a real..."
              disabled={loading}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
            />

            <Button
              variant="contained"
              onClick={handleSend}
              disabled={loading || !message.trim()}
              aria-label="Enviar"
            >
              <Icon name="send" />
            </Button>
          </InputContainer>
        </>
      )}
    </ChatContainer>
  );
};

export default Chat;
